import { Link } from "react-router-dom";
import {
  Radar, Stethoscope, Fingerprint, Mail, Globe, Bug, BookOpen, CheckSquare,
  Bot, Sparkles, Zap, Clock, ShieldCheck, FileSignature, History, Share2,
  Cpu, ListChecks, AlertOctagon, ArrowRight, Printer, Sliders, Users,
  Lock, Wrench, Terminal, KeyRound,
} from "lucide-react";

/**
 * Public quick-view — no login required. Beautiful marketing / overview page
 * for people who need to understand ForensiQ before signing in. Print-friendly
 * so it doubles as a one-page executive brief.
 */
export default function Marketing() {
  return (
    <div className="min-h-screen bg-[#060606] text-white print:bg-white print:text-black">
      <style>{`
        @media print {
          .no-print { display: none !important; }
          h1, h2, h3 { color: #000 !important; }
          .neon { color: #0057A0 !important; text-shadow: none !important; }
          .card { border: 1px solid #ddd !important; background: #fff !important; }
          .grid-bg { display: none !important; }
        }
      `}</style>

      {/* Grid background */}
      <div className="grid-bg fixed inset-0 pointer-events-none opacity-[0.06]"
           style={{
             backgroundImage: "linear-gradient(rgba(0,240,255,.5) 1px, transparent 1px), linear-gradient(90deg, rgba(0,240,255,.5) 1px, transparent 1px)",
             backgroundSize: "60px 60px",
           }} />

      {/* Top ribbon */}
      <header className="no-print border-b border-white/10 bg-[#0a0a0a]/80 backdrop-blur">
        <div className="max-w-7xl mx-auto flex items-center justify-between px-6 py-4">
          <Link to="/quick-view" className="flex items-center gap-2.5">
            <div className="relative w-8 h-8 border border-[#00F0FF] rounded-sm flex items-center justify-center">
              <Radar className="w-4 h-4 text-[#00F0FF]" strokeWidth={1.75} />
              <span className="absolute inset-0 blur-md bg-[#00F0FF]/20 -z-10" />
            </div>
            <div className="leading-none">
              <div className="font-display font-black text-lg tracking-tight">FORENSIQ</div>
              <div className="text-[10px] font-mono text-[#8E9196] uppercase tracking-widest">
                Investigation-first
              </div>
              <div className="text-[9px] font-mono text-[#52525B] uppercase tracking-widest mt-1" data-testid="brand-signature-marketing">
                Built by <span className="text-[#00F0FF]">Ahamed Fasudeen</span>
              </div>
            </div>
          </Link>
          <div className="flex items-center gap-3">
            <button onClick={() => window.print()}
              className="inline-flex items-center gap-1.5 border border-white/15 hover:border-[#00F0FF] hover:text-[#00F0FF] text-[#8E9196] px-3 py-2 rounded-sm font-mono uppercase text-[10px] tracking-widest">
              <Printer className="w-3 h-3" /> Print
            </button>
            <Link to="/login"
              className="inline-flex items-center gap-1.5 border border-[#00F0FF] bg-[#00F0FF] text-black hover:bg-[#00c8d1] px-4 py-2 rounded-sm font-mono uppercase text-[10px] tracking-widest">
              Sign in <ArrowRight className="w-3 h-3" />
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative max-w-6xl mx-auto px-6 pt-20 pb-16">
        <div className="text-[11px] font-mono uppercase tracking-widest text-[#00F0FF] mb-4 flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-[#00F0FF] animate-pulse" />
          Live · v6.5 · Feb 2026
        </div>
        <h1 className="font-display font-black text-6xl md:text-8xl leading-[0.9] max-w-4xl">
          Every attack has a signature.<br/>
          <span className="neon text-[#00F0FF]" style={{ textShadow: "0 0 40px rgba(0,240,255,0.4)" }}>
            Find it faster.
          </span>
        </h1>
        <p className="text-[#8E9196] text-xl mt-6 max-w-2xl leading-relaxed">
          ForensiQ is a multi-tenant SOC platform that folds SIEM, EDR triage,
          email forensics, threat mapping, CVE tracking and AI-driven root-cause
          diagnosis into one investigation-first console. Signed runbooks
          attach straight to your ticket.
        </p>

        <div className="mt-8 flex flex-wrap gap-3 no-print">
          <Link to="/login"
            className="inline-flex items-center gap-2 border border-[#00F0FF] bg-[#00F0FF] text-black hover:bg-[#00c8d1] px-5 py-3 rounded-sm font-mono uppercase text-xs tracking-widest font-bold">
            Get started <ArrowRight className="w-3.5 h-3.5" />
          </Link>
          <Link to="/guide"
            className="inline-flex items-center gap-2 border border-white/20 hover:border-white text-white px-5 py-3 rounded-sm font-mono uppercase text-xs tracking-widest">
            Full handbook
          </Link>
        </div>

        {/* Stats strip */}
        <div className="mt-14 grid grid-cols-2 md:grid-cols-4 gap-4 max-w-4xl">
          {[
            { n: "6-10", l: "aspect-by-aspect checks per RCA" },
            { n: "~30s", l: "AI verdict from a plain-English incident" },
            { n: "HMAC", l: "signed runbook attached to every ticket" },
            { n: "0",    l: "agents deployed on target endpoints" },
          ].map((s) => (
            <div key={s.l} className="card border border-white/10 rounded-sm p-4 bg-[#0a0a0a]">
              <div className="font-display font-black text-3xl text-[#00F0FF]">{s.n}</div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-[#8E9196] mt-1 leading-tight">
                {s.l}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Signature capability: RCA */}
      <section className="border-y border-white/10 bg-[#0a0a0a]/40 py-16">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-[11px] font-mono uppercase tracking-widest text-[#B57BFF] mb-3 flex items-center gap-2">
            <Stethoscope className="w-3.5 h-3.5" /> Signature capability
          </div>
          <h2 className="font-display font-black text-4xl md:text-5xl leading-tight">
            Root Cause Finder AI —<br/>
            <span className="text-[#B57BFF]">an agent-less sensor for every P1.</span>
          </h2>
          <p className="text-[#8E9196] text-lg mt-4 max-w-3xl leading-relaxed">
            Paste a plain-English incident description
            ("CrowdStrike agent stopped reporting since 3am, browser + email work fine").
            The AI reasons about the system like a senior SRE probing it with an
            agent-less sensor and returns a full report in ~30 seconds.
          </p>

          <div className="grid md:grid-cols-2 gap-4 mt-10">
            {[
              { icon: Cpu,           title: "Sensor probe",           body: "Scanned aspects chip list (network · DNS · port 443 · service state · agent integrity · TLS · proxy) with a confidence badge and reasoning line." },
              { icon: Stethoscope,   title: "Aspect-by-aspect checks", body: "6-10 concrete checks per incident with copy-pasteable verify commands for the target OS (PowerShell for Windows, bash for Linux/macOS)." },
              { icon: AlertOctagon,  title: "Ranked hypotheses",      body: "Each hypothesis with a confidence %, a why-line, and investigate + fix commands. First one is the top guess." },
              { icon: ListChecks,    title: "Guided fix runner",      body: "Tick OK / BAD / Skip on every check and every command as you run them; paste the raw output. Recompute recalculates the verdict live." },
              { icon: History,       title: "History drawer",         body: "Last 20 sessions per user auto-saved. Click to reload, delete individually. Falls back to tenant-wide default." },
              { icon: Share2,        title: "Tenant-scoped share",    body: "One-click copies a login-gated URL. Teammates in your tenant open it read-only — no re-run needed." },
              { icon: FileSignature, title: "Signed PDF runbook",     body: "One-click ~24KB PDF with verdict, sensor probe, all commands, HMAC-SHA256 signature block and QR verify URL on the last page." },
              { icon: ShieldCheck,   title: "Public verify endpoint", body: "Anyone with the printed URL can validate AUTHENTIC or TAMPERED without logging in — safe to include in incident tickets." },
            ].map((f) => (
              <div key={f.title} className="card border border-white/10 rounded-sm p-4 bg-[#060606] hover:border-[#B57BFF]/40 transition-colors">
                <f.icon className="w-5 h-5 text-[#B57BFF] mb-2" strokeWidth={1.5} />
                <div className="font-display font-bold text-white">{f.title}</div>
                <div className="text-sm text-[#8E9196] mt-1 leading-relaxed">{f.body}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Core capabilities grid */}
      <section className="max-w-6xl mx-auto px-6 py-16">
        <div className="text-[11px] font-mono uppercase tracking-widest text-[#00F0FF] mb-3">
          Everything else in the console
        </div>
        <h2 className="font-display font-black text-4xl leading-tight mb-10">
          One pane. Every SOC surface.
        </h2>
        <div className="grid md:grid-cols-3 gap-4">
          {CORE.map((c) => (
            <div key={c.title} className="card border border-white/10 rounded-sm p-5 bg-[#0a0a0a] hover:border-[#00F0FF]/40 transition-colors">
              <c.icon className="w-5 h-5 text-[#00F0FF] mb-3" strokeWidth={1.5} />
              <div className="font-display font-bold text-white text-lg">{c.title}</div>
              <div className="text-sm text-[#8E9196] mt-1.5 leading-relaxed">{c.body}</div>
              {c.badges && (
                <div className="mt-3 flex flex-wrap gap-1.5">
                  {c.badges.map((b) => (
                    <span key={b} className="font-mono text-[9px] uppercase tracking-widest text-[#00F0FF] border border-[#00F0FF]/40 rounded-sm px-1.5 py-0.5">
                      {b}
                    </span>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* Newest additions */}
      <section className="border-t border-white/10 bg-[#0a0a0a]/40 py-16">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-[11px] font-mono uppercase tracking-widest text-[#FFCC00] mb-3">
            What's new in v6.x
          </div>
          <h2 className="font-display font-black text-4xl leading-tight mb-10">
            Shipped this quarter.
          </h2>
          <div className="grid md:grid-cols-2 gap-4">
            {WHATS_NEW.map((c) => (
              <div key={c.title} className="card border border-white/10 rounded-sm p-5 bg-[#060606]">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 border border-[#FFCC00]/40 rounded-sm flex items-center justify-center bg-[#FFCC00]/[0.06] shrink-0">
                    <c.icon className="w-4 h-4 text-[#FFCC00]" strokeWidth={1.5} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-display font-bold text-white">{c.title}</div>
                    <div className="text-[10px] font-mono uppercase tracking-widest text-[#FFCC00]/70 mt-0.5">
                      {c.version}
                    </div>
                    <div className="text-sm text-[#8E9196] mt-2 leading-relaxed">{c.body}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Trust / security */}
      <section className="max-w-6xl mx-auto px-6 py-16">
        <div className="text-[11px] font-mono uppercase tracking-widest text-[#00F0FF] mb-3">
          Built for regulated teams
        </div>
        <h2 className="font-display font-black text-4xl leading-tight mb-10">
          Security is a first-class product surface.
        </h2>
        <div className="grid md:grid-cols-3 gap-4">
          {[
            { icon: Users,       title: "Multi-tenant RBAC",    body: "Per-tenant isolation with granular per-user permission grant/deny lists. Super-admin can operate across tenants; tenant admins stay pinned to their org." },
            { icon: Lock,        title: "TOTP MFA · opt-in",    body: "Every account may enrol MFA with 10 single-use recovery codes. Super-admins are not forced — they opt in when they're ready." },
            { icon: Wrench,      title: "Break-glass MFA",      body: "Tenant admins can force-disable MFA on locked-out users (bumps token_version, invalidates every stale session). Frozen founder account stays protected." },
            { icon: ShieldCheck, title: "HMAC-signed runbooks", body: "Every RCA PDF is HMAC-SHA256 sealed with a fail-closed signing key. Public verify endpoint returns AUTHENTIC / TAMPERED for any reviewer." },
            { icon: Terminal,    title: "Full audit trail",     body: "Every admin action (MFA disable, tenant switch, playbook run, key rotation) writes to the audit_events log with actor role + timestamp." },
            { icon: KeyRound,    title: "Rotate-safe keys",     body: "API keys are minted per scope, hashed at rest, rotatable without downtime, and revocable per-key." },
          ].map((f) => (
            <div key={f.title} className="card border border-white/10 rounded-sm p-4 bg-[#0a0a0a]">
              <f.icon className="w-5 h-5 text-[#00F0FF] mb-2" strokeWidth={1.5} />
              <div className="font-display font-bold text-white">{f.title}</div>
              <div className="text-sm text-[#8E9196] mt-1 leading-relaxed">{f.body}</div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="border-t border-white/10 py-16 no-print">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="font-display font-black text-4xl md:text-5xl mb-4">
            Ready to run your next P1 through it?
          </h2>
          <p className="text-[#8E9196] text-lg mb-8">
            Sign in with your tenant credentials. The Root Cause Finder AI is
            the first tab in your sidebar.
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            <Link to="/login"
              className="inline-flex items-center gap-2 border border-[#00F0FF] bg-[#00F0FF] text-black hover:bg-[#00c8d1] px-6 py-3 rounded-sm font-mono uppercase text-xs tracking-widest font-bold">
              Sign in <ArrowRight className="w-3.5 h-3.5" />
            </Link>
            <Link to="/guide"
              className="inline-flex items-center gap-2 border border-white/20 hover:border-white text-white px-6 py-3 rounded-sm font-mono uppercase text-xs tracking-widest">
              Read the full handbook
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-white/10 py-6">
        <div className="max-w-6xl mx-auto px-6 text-center">
          <div className="text-[10px] font-mono uppercase tracking-widest text-[#52525B]">
            Built by <span className="text-[#00F0FF]">Ahamed Fasudeen</span> · ForensiQ v6.5 · Feb 2026
          </div>
        </div>
      </footer>
    </div>
  );
}

/* ------------------------- Content constants ------------------------- */

const CORE = [
  { icon: Fingerprint, title: "Investigations",     body: "Case management with a timeline, IOCs, suspects, notes and every AI/playbook run pinned to the case.",              badges: ["Case timeline", "IOC list"] },
  { icon: Mail,        title: "Email Investigator", body: "Hook up IMAP/SMTP, parse messages, score SPF/DKIM/DMARC alignment, extract YARA-matched attachments.",              badges: ["Phishing", "BEC", "Header chain"] },
  { icon: Globe,       title: "Threat Map",         body: "2D + 3D world map of live threats streamed from Feodo, Blocklist.de, URLhaus, Spamhaus, Emerging Threats and Tor.",  badges: ["Live", "3D globe"] },
  { icon: Bug,         title: "CVE Tracker",        body: "Live feed from CIRCL + NVD + CISA KEV. Platform sunburst (Windows / Linux / Cloud). Egress-diagnostic endpoint.",   badges: ["KEV", "Sunburst"] },
  { icon: Bot,         title: "Threat Hunting AI",  body: "Guided hunts — pick a hypothesis, the AI writes queries against your data lakes and returns triaged findings." },
  { icon: Sparkles,    title: "Ask ForensiQ",       body: "Natural-language Q&A over your tenant's logs, cases and knowledge base — powered by a universal LLM key." },
  { icon: BookOpen,    title: "Playbooks",          body: "SOAR playbooks chained across your connectors. Manual, cron or webhook-triggered with per-run audit." },
  { icon: CheckSquare, title: "Compliance",         body: "NIST CSF, ISO 27001, PCI-DSS, HIPAA, SOC 2 with control-level ownership, evidence and scheduled re-checks." },
  { icon: Clock,       title: "Shift Briefings",    body: "Every 8h, ForensiQ compiles the incoming shift's brief — open incidents, hot IOCs, KEV adds, MTTR delta." },
];

const WHATS_NEW = [
  { icon: Stethoscope,   version: "v6.1", title: "Root Cause Finder AI",     body: "Agent-less sensor with 6-10 aspect-by-aspect checks and ranked hypotheses per incident. Claude Haiku 4.5 via a universal LLM key." },
  { icon: ListChecks,    version: "v6.2", title: "Guided Fix Runner",        body: "Tick every check + command as OK / BAD / Skip, paste raw output, recompute — verdict escalates live from 78% MOST LIKELY to 92% ROOT CAUSE CONFIRMED." },
  { icon: History,       version: "v6.3", title: "History + tenant share",   body: "Last 20 RCA sessions per user auto-saved. Share button copies a tenant-gated URL any teammate can open read-only." },
  { icon: FileSignature, version: "v6.4", title: "Signed PDF Runbook",       body: "HMAC-SHA256 sealed PDF with QR verify code — attach to any incident ticket; public /verify returns AUTHENTIC / TAMPERED." },
  { icon: Wrench,        version: "v6.5", title: "Break-glass MFA fix",      body: "Any tenant admin can now force-disable MFA on users in their tenant. Bumps token_version, invalidates every stale session. Frozen founder still protected." },
  { icon: Sliders,       version: "v6.5", title: "Customize sidebar",        body: "Drag any tab to reorder or hide the ones you never use. Per-user override on top of a tenant-wide default set by admins." },
];
